import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Container,
    Typography,
    Card,
    CardContent,
    Box,
    Button,
    Grid,
    makeStyles,
    LinearProgress,
    Chip,
    CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Upload, Camera, ArrowLeft, Sparkles, CheckCircle, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useImageUpload } from '../../hooks/useImageUpload';
import { config } from '../../Constants';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    container: {
        maxWidth: '800px',
    },
    header: {
        textAlign: 'center',
        color: 'white',
        marginBottom: theme.spacing(4),
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(1),
        fontSize: '2.5rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '2rem',
        },
    },
    subtitle: {
        fontSize: '1.1rem',
        opacity: 0.9,
        marginBottom: theme.spacing(2),
    },
    card: {
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        marginBottom: theme.spacing(3),
    },
    uploadArea: {
        border: '3px dashed #667eea',
        borderRadius: 16,
        padding: theme.spacing(6),
        textAlign: 'center',
        backgroundColor: '#f8fafc',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
            borderColor: '#5a67d8',
            backgroundColor: '#f1f5f9',
        },
        '&.dragover': {
            borderColor: '#4c51bf',
            backgroundColor: '#e2e8f0',
        },
    },
    uploadIcon: {
        marginBottom: theme.spacing(2),
        color: '#667eea',
    },
    progressCard: {
        backgroundColor: '#e8f5e8',
        border: '2px solid #4CAF50',
    },
    imagePreview: {
        width: '100%',
        height: 120,
        objectFit: 'cover',
        borderRadius: 8,
    },
    backButton: {
        color: 'white',
        marginBottom: theme.spacing(2),
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
        },
    },
    startButton: {
        padding: theme.spacing(2, 6),
        fontSize: '1.2rem',
        fontWeight: 600,
        borderRadius: 50,
        background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #4CAF50 60%, #45a049 100%)',
            transform: 'scale(1.05)',
        },
        '&:disabled': {
            background: '#ccc',
        },
    },
    uploadButton: {
        padding: theme.spacing(1.5, 4),
        fontSize: '1rem',
        fontWeight: 600,
        borderRadius: 25,
        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
        color: 'white',
        marginTop: theme.spacing(2),
        '&:hover': {
            background: 'linear-gradient(45deg, #667eea 60%, #764ba2 100%)',
            transform: 'scale(1.02)',
        },
        '&:disabled': {
            background: '#ccc',
        },
    },
    errorAlert: {
        marginBottom: theme.spacing(2),
    },
    imageStatus: {
        position: 'absolute',
        top: 4,
        left: 4,
        backgroundColor: 'rgba(0,0,0,0.8)',
        borderRadius: '50%',
        padding: 2,
    },
}));

const PhotoUpload = () => {
    const classes = useStyles();
    const history = useHistory();
    const { currentUser } = useAuth();
    const { userProfile } = useUser();
    
    const {
        uploadedImages,
        uploading,
        uploadProgress,
        errors,
        stats,
        loading,
        addImages,
        removeImage,
        uploadImages,
        clearErrors
    } = useImageUpload();

    const handleBack = () => {
        history.push('/guide');
    };

    const handleFileSelect = useCallback((event) => {
        const files = Array.from(event.target.files);
        addImages(files);
        clearErrors();
    }, [addImages, clearErrors]);

    const handleDragOver = useCallback((event) => {
        event.preventDefault();
        event.currentTarget.classList.add('dragover');
    }, []);

    const handleDragLeave = useCallback((event) => {
        event.preventDefault();
        event.currentTarget.classList.remove('dragover');
    }, []);

    const handleDrop = useCallback((event) => {
        event.preventDefault();
        event.currentTarget.classList.remove('dragover');
        const files = Array.from(event.dataTransfer.files);
        addImages(files);
        clearErrors();
    }, [addImages, clearErrors]);

    const handleUpload = useCallback(async () => {
        const result = await uploadImages();
        if (result && result.successCount > 0) {
            // Show success message or navigate
            console.log(`Successfully uploaded ${result.successCount} images`);
        }
    }, [uploadImages]);

    const startTraining = useCallback(() => {
        // Only allow training if all images are uploaded successfully
        const uploadedCount = stats.uploaded;
        if (uploadedCount >= config.maxSelfies) {
            console.log('Starting training with', uploadedCount, 'images');
            history.push('/training');
        }
    }, [stats.uploaded, history]);

    // Get status icon for each image
    const getStatusIcon = (image) => {
        switch (image.status) {
            case 'uploaded':
                return <CheckCircle size={16} color="#4CAF50" />;
            case 'uploading':
                return <div style={{ width: 16, height: 16, border: '2px solid #667eea', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />;
            case 'error':
                return <AlertCircle size={16} color="#f44336" />;
            default:
                return null;
        }
    };

    console.log(uploadedImages);

    return (
        <Box className={classes.root}>
            <Container className={classes.container}>
                <Button
                    startIcon={<ArrowLeft size={20} />}
                    onClick={handleBack}
                    className={classes.backButton}
                >
                    Back to Guide
                </Button>

                <Box className={classes.header}>
                    <Typography variant="h1" className={classes.title}>
                        Upload Your Photos
                    </Typography>
                    <Typography variant="h2" className={classes.subtitle}>
                        Upload {config.maxSelfies} high-quality selfies to train your AI model
                    </Typography>
                    <Typography variant="body1" style={{ opacity: 0.8 }}>
                        Welcome, {userProfile?.name || currentUser?.email}! You have {userProfile?.tokens || 0} tokens.
                    </Typography>
                </Box>

                {/* Loading State */}
                {loading && (
                    <Card className={classes.card}>
                        <CardContent>
                            <Box textAlign="center" padding={4}>
                                <CircularProgress size={40} style={{ marginBottom: 16 }} />
                                <Typography variant="body1" color="textSecondary">
                                    Loading your photos...
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Error Messages */}
                {!loading && errors.length > 0 && (
                    <Alert severity="error" className={classes.errorAlert}>
                        <Typography variant="h6" style={{ marginBottom: 8 }}>
                            Upload Errors:
                        </Typography>
                        {errors.map((error, index) => (
                            <Box key={index} style={{ marginBottom: 4 }}>
                                <strong>{error.fileName}:</strong> {error.errors.join(', ')}
                            </Box>
                        ))}
                        <Button 
                            size="small" 
                            onClick={clearErrors}
                            style={{ marginTop: 8, color: '#d32f2f' }}
                        >
                            Clear Errors
                        </Button>
                    </Alert>
                )}

                {/* Progress Card */}
                {!loading && (
                <Card className={`${classes.card} ${stats.uploaded >= config.maxSelfies ? classes.progressCard : ''}`}>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
                            <Typography variant="h6">
                                Upload Progress: {stats.total}/{config.maxSelfies}
                            </Typography>
                            <Box display="flex" gap={1}>
                                {stats.pending > 0 && <Chip label={`${stats.pending} pending`} size="small" />}
                                {stats.uploading > 0 && <Chip label={`${stats.uploading} uploading`} size="small" color="primary" />}
                                {stats.uploaded > 0 && <Chip label={`${stats.uploaded} uploaded`} size="small" style={{ backgroundColor: '#4CAF50', color: 'white' }} />}
                                {stats.errors > 0 && <Chip label={`${stats.errors} errors`} size="small" color="secondary" />}
                            </Box>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={stats.progress} 
                            style={{ height: 8, borderRadius: 4, marginBottom: 16 }}
                        />
                        {stats.uploaded >= config.maxSelfies && (
                            <Box textAlign="center">
                                <Typography variant="body1" style={{ color: '#2e7d32', fontWeight: 600 }}>
                                    ✅ Ready to start training your AI model!
                                </Typography>
                            </Box>
                        )}
                        {stats.pending > 0 && (
                            <Box textAlign="center">
                                <Button
                                    className={classes.uploadButton}
                                    onClick={handleUpload}
                                    disabled={uploading}
                                    startIcon={uploading ? null : <Upload size={20} />}
                                >
                                    {uploading ? 'Uploading...' : `Upload ${stats.pending} Photos`}
                                </Button>
                            </Box>
                        )}
                    </CardContent>
                </Card>
                )}

                {/* Upload Area */}
                {!loading && stats.canUpload && (
                    <Card className={classes.card}>
                        <CardContent>
                            <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={{ display: 'none' }}
                                id="file-upload"
                            />
                            <label htmlFor="file-upload">
                                <Box
                                    className={classes.uploadArea}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <Upload size={60} className={classes.uploadIcon} />
                                    <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                                        Drag & drop your photos here
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" style={{ marginBottom: 16 }}>
                                        or click to browse your files
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Accepts JPG, PNG, HEIC • Max 10MB per file
                                    </Typography>
                                </Box>
                            </label>
                        </CardContent>
                    </Card>
                )}

                {/* Uploaded Images Grid */}
                {!loading && uploadedImages.length > 0 && (
                    <Card className={classes.card}>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
                                <Typography variant="h6">
                                    Photos ({uploadedImages.length})
                                </Typography>
                                {uploadedImages.some(img => img.isExisting) && (
                                    <Chip 
                                        label="Including previous uploads" 
                                        size="small" 
                                        style={{ backgroundColor: '#4CAF50', color: 'white' }}
                                    />
                                )}
                            </Box>
                            <Grid container spacing={2}>
                                {uploadedImages.map((image) => (
                                    <Grid item xs={6} sm={4} md={3} key={image.id}>
                                        <Box position="relative">
                                            <img
                                                src={image.preview}
                                                alt={image.name}
                                                className={classes.imagePreview}
                                            />
                                            
                                            {/* Status Icon */}
                                            <Box className={classes.imageStatus}>
                                                {getStatusIcon(image)}
                                            </Box>
                                            
                                            {/* Existing Image Badge */}
                                            {image.isExisting && (
                                                <Box
                                                    position="absolute"
                                                    bottom={4}
                                                    right={4}
                                                    backgroundColor="rgba(76, 175, 80, 0.9)"
                                                    borderRadius={1}
                                                    padding={0.5}
                                                    fontSize="0.7rem"
                                                    color="white"
                                                    fontWeight={600}
                                                >
                                                    Previously Uploaded
                                                </Box>
                                            )}
                                            
                                            {/* Progress bar for uploading images */}
                                            {image.status === 'uploading' && uploadProgress[image.id] && (
                                                <Box
                                                    position="absolute"
                                                    bottom={4}
                                                    left={4}
                                                    right={4}
                                                    backgroundColor="rgba(0,0,0,0.8)"
                                                    borderRadius={1}
                                                    padding={0.5}
                                                >
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={uploadProgress[image.id]}
                                                        style={{ height: 4, borderRadius: 2 }}
                                                    />
                                                </Box>
                                            )}
                                            
                                            {/* Remove Button */}
                                            {image.status !== 'uploading' && (
                                                <Button
                                                    size="small"
                                                    onClick={() => removeImage(image.id)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        minWidth: 'auto',
                                                        backgroundColor: 'rgba(0,0,0,0.7)',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        width: 24,
                                                        height: 24,
                                                    }}
                                                >
                                                    <X size={14} />
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Start Training Button */}
                {!loading && stats.uploaded >= config.maxSelfies && (
                    <Box textAlign="center">
                        <Button
                            className={classes.startButton}
                            size="large"
                            onClick={startTraining}
                            startIcon={<Sparkles size={24} />}
                        >
                            Start AI Training ({config.trainingTimeEstimate})
                        </Button>
                    </Box>
                )}
            </Container>
        </Box>
    );
};

export default PhotoUpload;